<script setup lang="ts">
/**
 * SiteGrid — 支持站点网格（9 站 3×3，Nexus Cyberpunk 风格）
 * - 玻璃霓虹卡：surface-2 实底 + rule 描边 + 霓虹光晕 + 圆角 12px（玻璃态仅悬浮层，卡用实底）
 * - 白底 logo 安全区：部分站 logo 含黑色元素，深色底上不可读，
 *   白底仅限 logo 块（刻意保留），卡片本体保持霓虹深色。见 docs/maindesign.md §5。
 * - mono 编号 01-09（Orbitron HUD 感）+ 站点名（IBM Plex Sans 700）
 * - 桌面/平板/移动端均 3 列（9 = 3×3 恒整齐），hover 出 accent 光晕
 */
defineProps<{
  items: { name: string; key: string; image?: string }[]
}>()

function getIndex(idx: number): string {
  return String(idx + 1).padStart(2, '0')
}

function getInitial(name: string): string {
  return (name || '?').trim().charAt(0).toUpperCase()
}
</script>

<template>
  <div class="site-grid">
    <div
      v-for="(item, idx) in items"
      :key="item.key"
      class="site-grid__item yfb-press"
      :aria-label="item.name"
    >
      <span class="site-grid__idx" aria-hidden="true">{{ getIndex(idx) }}</span>
      <div class="site-grid__logo">
        <img
          v-if="item.image"
          :src="item.image"
          :alt="item.name"
          class="site-grid__img"
          loading="lazy"
        />
        <span v-else class="site-grid__placeholder" aria-hidden="true">{{ getInitial(item.name) }}</span>
      </div>
      <span class="site-grid__name">{{ item.name }}</span>
    </div>
  </div>
</template>

<style scoped>
.site-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--yfb-sp-4);
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.site-grid__item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--yfb-sp-3);
  padding: var(--yfb-sp-6) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-1);
  transition: transform var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out, border-color var(--yfb-anim-fast) ease-out;
}

.site-grid__item:hover {
  transform: translateY(-2px);
  border-color: var(--yfb-accent);
  box-shadow: var(--yfb-shadow-hit);
}

.site-grid__idx {
  position: absolute;
  top: var(--yfb-sp-2);
  right: var(--yfb-sp-3);
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-xs);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-mono);
  color: var(--yfb-accent);
  text-shadow: 0 0 8px var(--yfb-glow-soft);
  opacity: 0.9;
}

.site-grid__logo {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  /* 白底安全区（刻意保留）：部分站 logo 含黑色元素，深色底上不可读。
     白底仅限 logo 块，卡片本体保持霓虹深色。 */
  background-color: #ffffff;
  border-radius: var(--yfb-radius-md);
  box-shadow: 0 0 0 1px var(--yfb-rule), 0 0 10px var(--yfb-glow-soft);
  transition: box-shadow var(--yfb-anim-fast) ease-out;
}

.site-grid__item:hover .site-grid__logo {
  box-shadow: 0 0 0 1px var(--yfb-accent), 0 0 14px var(--yfb-glow);
}

.site-grid__img {
  display: block;
  width: 44px;
  height: 44px;
  object-fit: contain;
}

.site-grid__placeholder {
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-xl);
  font-weight: 700;
  color: var(--yfb-accent);
}

.site-grid__name {
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-button);
  line-height: 1.3;
  color: var(--yfb-ink);
  text-align: center;
}

@media (max-width: 1023px) {
  .site-grid {
    gap: var(--yfb-sp-3);
  }
  .site-grid__item {
    padding: var(--yfb-sp-5) var(--yfb-sp-3);
  }
  .site-grid__logo {
    width: 56px;
    height: 56px;
  }
  .site-grid__img {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 767px) {
  .site-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--yfb-sp-3);
    padding: 0 16px;
  }
  .site-grid__item {
    padding: var(--yfb-sp-4) var(--yfb-sp-2);
    gap: var(--yfb-sp-2);
  }
  .site-grid__logo {
    width: 44px;
    height: 44px;
    border-radius: var(--yfb-radius-sm);
  }
  .site-grid__img {
    width: 32px;
    height: 32px;
  }
  .site-grid__name {
    font-size: var(--yfb-fs-xs);
  }
  .site-grid__idx {
    top: var(--yfb-sp-1);
    right: var(--yfb-sp-2);
  }
}
</style>
